import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Badge from "@mui/material/Badge";
import moment from "moment";
import ItemDetails from "./ItemDetails/ItemDetails";
import useQueryParams from "../../../../../../hooks/useQueryParams";
import Empty from "./Empty";
import InternalWorkOrderItem from "./InternalWorkOrderItem";
import Footer from "../../../../../../components/wizard/Footer/Footer";
import useMutation from "../../../../../../hooks/useMutation";
import { UPDATE_INTERNAL_WORK_ORDER_MUTATION } from "../../../../../../constants/graphql/mutations";
import { serverFormatSeconds } from "../../../../../../utils/dates";
import { ProjectProvider } from "../../../../../../contexts/project";
import useInventoryItemsFilter from "../../../../../../hooks/useInventoryItemsFilter";
import SearchButton from "../../../../../../components/wizard/SearchButton";
import Header from "../../../../../../components/wizard/Header";
import SearchTextField from "../../../../../../components/wizard/SearchTextField";
import useHasAdditionalCharges from "src/components/capture/ReceivingWizard/hooks/useHasAdditionalCharges";
import useHasEnabledIntegration from "src/hooks/useHasEnabledIntegration";
import { nextAfterItems } from "src/utils/integrationWizardSteps";
import InternalBillingQuantitiesModal from "../InternalBillingQuantitiesModal";
import useAuth from "src/hooks/useAuth";
import IWOMasterScanOrEnterModal from "./IWOMasterScanOrEnterModal";

export default function Items({ project, internalWorkOrder }) {
  const navigate = useNavigate();
  const params = useQueryParams();
  const assetId = params.get("assetId");
  const internalWorkOrderItemId = params.get("internalWorkOrderItemId");
  const inventoryItems = internalWorkOrder?.internalWorkOrderItems || [];
  const [showSearch, setShowSearch] = useState(false);
  const [openBillingQuantitiesModal, setOpenBillingQuantitiesModal] = useState(false);
  const [openScanOutModal, setOpenScanOutModal] = useState(false);
  const { user } = useAuth();
  const { settings } = user.company;
  const hasAdditionalCharges = useHasAdditionalCharges(project, "outbound");
  const { hasIntegration } = useHasEnabledIntegration(project);
  const totalSteps = (hasAdditionalCharges ? 4 : 3) + (hasIntegration ? 1 : 0);
  const internalBillingType = internalWorkOrder?.billingType;
  const internalWarehouses = internalWorkOrder?.warehousesFromInternalItems || [];
  const allWarehousesExcluded =
    internalWarehouses.length > 0 && internalWarehouses.every(w => w.excludeFromBilling);
  const shouldShowBillingQuantities =
    !allWarehousesExcluded &&
    (internalBillingType === "Time and Materials (Blended Rate)" ||
      internalBillingType === "Time and Materials (Itemized Rates)");
  
  // Sort items by rack location (kits at the end, non-kits sorted alphabetically by rack location)
  const sortedItems = [...inventoryItems].sort((a, b) => {
    const aIsKit = a.asset?.type === "Kit";
    const bIsKit = b.asset?.type === "Kit";

    if (aIsKit && !bIsKit) return 1;
    if (!aIsKit && bIsKit) return -1;
    if (aIsKit && bIsKit) return 0;

    const aLocation = a.rackLocations[0]?.warehouse_locations?.[0]?.location_name || "";
    const bLocation = b.rackLocations[0]?.warehouse_locations?.[0]?.location_name || "";
    return aLocation.localeCompare(bLocation);
  });

  const {
    searchTerm,
    filteredItems: internalWorkOrderItems,
    handleSearchChange,
  } = useInventoryItemsFilter(sortedItems);

  const [saveInternalWorkOrder] = useMutation(UPDATE_INTERNAL_WORK_ORDER_MUTATION);
  const allItemsCompleted = sortedItems.every(item => item.pickedStatus && item.completedStatus);

  const handleToggle = () => {
    const params = new URLSearchParams(location.search);
    params.delete("assetId");
    params.delete("internalWorkOrderItemId");
    params.delete("addShipmentItem");
    params.delete("itemLookup");
    params.delete("rackLocationsChanges");
    params.delete("duplicate");
    params.delete("newItem");
    navigate({ search: params.toString() });
  };

  const handleContinue = () => {
    if (!internalWorkOrder.pickingCompleteTime) {
      const pickingCompleteTime = moment().format(serverFormatSeconds);
      saveInternalWorkOrder({
        variables: {
          id: internalWorkOrder.id,
          input: { pickingCompleteTime },
        },
      });
    }
    if (shouldShowBillingQuantities) {
      setOpenBillingQuantitiesModal(true);
    } else {
      handleComplete();
    }
  };
  const handleSaveBillingQuantities = () => {
    setOpenBillingQuantitiesModal(false);
    handleComplete();
  };
  const handleComplete = () => {
    const params = new URLSearchParams(window.location.search);
    params.set(
      "step",
      nextAfterItems({
        hasIntegration,
        hasCharges: hasAdditionalCharges && !allWarehousesExcluded,
        chargeStep: "charges",
        fallbackStep: "confirmation",
      })
    );
    navigate({ search: params.toString() });
  };

  const handleBack = () => {
    const params = new URLSearchParams(window.location.search);
    params.set("step", "details");
    navigate({ search: params.toString() });
  };

  const handleSelect = value => {
    const pathname = window.location.pathname;
    const params = new URLSearchParams(location.search);
    params.set("internalWorkOrderItemId", value.id);
    params.set("assetId", value.asset.id);
    navigate({ pathname, search: params.toString() });
  };

  const handleToggleSearch = () => {
    if (showSearch) {
      // Clear search when closing
      handleSearchChange({ target: { value: "" } });
    }
    setShowSearch(!showSearch);
  };

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-between" minHeight="100%">
      <Header
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <span>Internal Order Items</span>
            <Badge
              badgeContent={inventoryItems.length || 0}
              max={999999}
              sx={{
                '& .MuiBadge-badge': {
                  position: 'relative',
                  transform: 'none',
                  backgroundColor: 'transparent',
                  color: '#666',
                  fontSize: '14px',
                  height: '24px',
                  minWidth: '24px',
                  borderRadius: '50%',
                  fontWeight: 500,
                  border: '1.5px solid #999',
                }
              }}
            />
          </Box>
        }
        actions={<SearchButton open={showSearch} onClick={handleToggleSearch} />}
        info={
          settings.scan_out_modal ? (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setOpenScanOutModal(true)}
              disabled={allItemsCompleted}
              sx={{
                ...(allItemsCompleted && {
                  backgroundColor: "#e0e0e0 !important",
                  color: "#9e9e9e",
                  cursor: "not-allowed",
                }),
              }}
            >
              Scan Out
            </Button>
          ) : (
            internalWorkOrder.internalWorkOrderNumber
          )
        }
      />
      {showSearch && (
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "white",
            borderBottom: "1px solid #e0e0e0",
          }}
          px={2}
          py={1.5}
        >
          <SearchTextField value={searchTerm} onChange={handleSearchChange} />
        </Box>
      )}
      <Box p={2}>
        <Box display="flex" flexDirection="column" alignItems="center">
          {sortedItems.length === 0 ? (
            <Empty />
          ) : internalWorkOrderItems.length === 0 && searchTerm ? (
            <Box pt={4} pb={4} display="flex" justifyContent="center" width="100%">
              <Typography variant="body2" color="textSecondary">
                No items found matching "{searchTerm}"
              </Typography>
            </Box>
          ) : (
            <Box width="100%" minHeight={300}>
              {internalWorkOrderItems.map(value => (
                <InternalWorkOrderItem
                  key={value.id}
                  value={value}
                  project={project}
                  onSelect={handleSelect}
                  internalWorkOrderId={internalWorkOrder.id}
                  warehouses={internalWorkOrder?.warehousesFromInternalItems}
                />
              ))}
            </Box>
          )}
          <Box mt={4}>
            {assetId && (
              <ProjectProvider id={internalWorkOrder.project.id}>
                <ItemDetails
                  id={assetId}
                  project={project}
                  internalWorkOrder={internalWorkOrder}
                  internalWorkOrderItem={sortedItems.find(v => v.id === internalWorkOrderItemId)}
                  onClose={handleToggle}
                />
              </ProjectProvider>
            )}
          </Box>
        </Box>
      </Box>
      <Box mt={2}>
        <Footer
          step={1}
          steps={totalSteps}
          startTime={internalWorkOrder?.pickingStartTime}
          completeTime={internalWorkOrder?.pickingCompleteTime}
        >
          <div>
            <Button onClick={handleBack} variant="outlined" sx={{ mr: 1, textTransform: "none" }}>
              Back
            </Button>
            <Button
              type="submit"
              disabled={false}
              variant="contained"
              sx={{ textTransform: "none" }}
              onClick={handleContinue}
            >
              Next
            </Button>
          </div>
        </Footer>
      </Box>
      {openBillingQuantitiesModal && (
        <InternalBillingQuantitiesModal
          internalWorkOrder={internalWorkOrder}
          onSave={handleSaveBillingQuantities}
          onClose={() => setOpenBillingQuantitiesModal(false)}
        />
      )}
      {openScanOutModal && (
        <IWOMasterScanOrEnterModal
          iwoItems={sortedItems}
          projectId={internalWorkOrder.project?.id}
          internalWorkOrder={internalWorkOrder}
          onClose={() => setOpenScanOutModal(false)}
          onScanComplete={() => setOpenScanOutModal(false)}
        />
      )}
    </Box>
  );
}
